import { Close as CloseIcon, HourglassBottom, Search as SearchIcon } from '@mui/icons-material';
import { Box, Chip, IconButton, InputAdornment, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { CountriesTablePaginated, CountryInfoDialog, CountryInfoSubmitArgs, countryModel } from 'entities/country';
import { EditDiseaseDialog, diseaseModel } from 'entities/disease';
import { userModel } from 'entities/user';

import { CountryPeopleListItem } from 'shared/api';
import { useDebouncedSearch } from 'shared/lib';
import { LoadingProgress } from 'shared/ui';

/**
 * Стили чипа-фильтра. Объект намеренно постоянный, а активное состояние выражено
 * атрибутом data-active, а не ветвлением внутри sx: при ветвлении emotion генерирует
 * новый класс на каждое переключение и переиспользует хеш со старым правилом —
 * в браузере это давало заливку у выключенного фильтра и её отсутствие у включённого.
 *
 * Селектор с атрибутом заодно даёт нужную специфичность: фон у MUI задан селектором
 * из двух классов (.MuiChip-root.MuiChip-outlined), одиночный класс от sx его не перебивает.
 *
 * Заливка — светлый оттенок warning, а не warning.main: белый текст на #ed6c02 даёт 3.11:1,
 * здесь же подпись остаётся тёмной и читаемой.
 */
const NEEDS_CHECK_CHIP_SX = {
  whiteSpace: 'nowrap',
  borderColor: 'warning.dark',
  '& .MuiChip-label': { color: 'text.primary' },
  '& .MuiChip-icon': { color: 'warning.dark' },
  '&:hover': { backgroundColor: 'rgba(237, 108, 2, 0.08)' },
  '&[data-active="true"]': {
    borderWidth: 2,
    fontWeight: 600,
    backgroundColor: 'rgba(237, 108, 2, 0.16)',
  },
  '&[data-active="true"]:hover': { backgroundColor: 'rgba(237, 108, 2, 0.26)' },
} as const;

export const CountriesTab = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Получаем параметры из URL или используем значения по умолчанию
  const [filter, setFilter] = useState(searchParams.get('filter') ?? '');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') ?? 'countryName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    (searchParams.get('sortOrder') as 'asc' | 'desc') ?? 'asc',
  );
  const [page, setPage] = useState(parseInt(searchParams.get('page') ?? '1', 10));
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get('limit') ?? '20', 10));
  const [needsCheckOnly, setNeedsCheckOnly] = useState(searchParams.get('needsCheck') === 'true');

  // Состояние для редактирования болезни
  const [selectedDiseaseId, setSelectedDiseaseId] = useState<string | null>(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
  const [loadingDiseaseId, setLoadingDiseaseId] = useState<string | null>(null);

  // Состояние для редактирования паспорта страны.
  // Храним снимок строки, а не код: если строка исчезнет из отфильтрованного списка
  // (например, из-за срабатывания debounce поиска), открытый диалог не должен пропасть
  // вместе с несохранённым текстом.
  const [selectedInfoCountry, setSelectedInfoCountry] = useState<CountryPeopleListItem | null>(null);

  const { data: userData } = userModel.useUser();

  // Мутации
  const {
    mutate: updateDisease,
    isSuccess: isUpdateSuccess,
    isLoading: isUpdating,
  } = diseaseModel.updateDiseaseAsync();

  // Используем debounce для фильтра
  const debouncedFilter = useDebouncedSearch(filter, 400);

  // Создаем объект параметров для API
  const params = useMemo(
    () => ({
      filter: debouncedFilter || undefined,
      sortBy,
      sortOrder,
      page,
      limit: rowsPerPage,
      needsCheck: needsCheckOnly || undefined,
    }),
    [debouncedFilter, sortBy, sortOrder, page, rowsPerPage, needsCheckOnly],
  );

  const { isLoading, isFetching } = countryModel.getCountriesPeopleAllAsync(params)(dispatch);
  const { countriesList, pagination } = countryModel.useCountriesPeopleList();

  const { data: needsCheckCount = 0 } = countryModel.useCountriesNeedsCheckCount('people');
  const { mutate: saveCountryInfo, isLoading: isSavingInfo } = countryModel.useSaveCountryInfo('people');

  // Обновляем URL при изменении параметров
  useEffect(() => {
    const newParams: Record<string, string> = {
      page: page.toString(),
      limit: rowsPerPage.toString(),
      sortBy,
      sortOrder,
    };

    if (debouncedFilter) newParams.filter = debouncedFilter;
    if (needsCheckOnly) newParams.needsCheck = 'true';

    setSearchParams(newParams, { replace: true });
  }, [debouncedFilter, sortBy, sortOrder, page, rowsPerPage, needsCheckOnly, setSearchParams]);

  // Закрываем диалог при успешном обновлении и обновляем таблицу
  useEffect(() => {
    if (isUpdateSuccess) {
      setSelectedDiseaseId(null);
      setSelectedCountryCode('');
      // Обновляем таблицу стран, так как болезнь могла измениться
      void queryClient.invalidateQueries([countryModel.COUNTRIES_PEOPLE_ALL_QUERY_KEY]);
    }
  }, [isUpdateSuccess, queryClient]);

  // Обработчики
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
    setPage(1);
  }, []);

  const handleClearFilter = useCallback(() => {
    setFilter('');
    setPage(1);
  }, []);

  const handleSortChange = useCallback(
    (columnId: string) => {
      if (sortBy === columnId) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(columnId);
        setSortOrder('asc');
      }
      setPage(1);
    },
    [sortBy, sortOrder],
  );

  const handleDiseaseClick = useCallback((diseaseId: string, countryCode: string) => {
    setLoadingDiseaseId(diseaseId);
    setSelectedDiseaseId(diseaseId);
    setSelectedCountryCode(countryCode);
  }, []);

  const handleDataLoaded = useCallback(() => {
    setLoadingDiseaseId(null);
  }, []);

  const handleCloseDiseaseDialog = useCallback(() => {
    setSelectedDiseaseId(null);
    setSelectedCountryCode('');
    setLoadingDiseaseId(null);
  }, []);

  /**
   * Чип «N need a calendar check» — это и счётчик, и переключатель фильтра.
   * При включении сбрасываем текстовый фильтр: счётчик считается без filter,
   * иначе он разойдётся со списком, отфильтрованным по старому поисковому запросу.
   */
  const handleToggleNeedsCheck = useCallback(() => {
    setNeedsCheckOnly((prev) => {
      if (!prev) setFilter('');

      return !prev;
    });
    setPage(1);
  }, []);

  const handleCountryInfoClick = useCallback(
    (countryCode: string) => {
      // Снимок строки берём один раз в момент клика и кладём в стейт —
      // а не выводим через useMemo от countriesList, который меняется уже после debounce
      const country = countriesList.find((item) => item.countryCode === countryCode) ?? null;
      if (country) setSelectedInfoCountry(country);
    },
    [countriesList],
  );

  const handleCloseInfoDialog = useCallback(() => {
    setSelectedInfoCountry(null);
  }, []);

  const handleSaveCountryInfo = useCallback(
    (args: CountryInfoSubmitArgs) => {
      saveCountryInfo(args, {
        onSuccess: () => setSelectedInfoCountry(null),
        onError: (error) => {
          // 404 — страна удалена другим админом: сообщение уже показал глобальный onError,
          // здесь просто закрываем диалог (список инвалидируется в onSettled модели)
          if (countryModel.isCountryNotFoundError(error)) setSelectedInfoCountry(null);
        },
      });
    },
    [saveCountryInfo],
  );

  // Показываем LoadingProgress только при первой загрузке
  if (isLoading && !countriesList.length) return <LoadingProgress />;

  return (
    <Box display="flex" flexDirection="column">
      <Paper>
        <Box display="flex" alignItems="center" p="8px 16px">
          <Typography variant="h6">Countries control panel</Typography>
          <Box display="flex" alignItems="center" ml={3} mr="auto">
            <TextField
              sx={{ minWidth: '350px', width: '100%' }}
              placeholder="Enter country name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filter && (
                  <IconButton onClick={handleClearFilter}>
                    <CloseIcon />
                  </IconButton>
                ),
              }}
              size="small"
              value={filter}
              onChange={handleFilterChange}
            />
            <Typography px={2} whiteSpace="nowrap">
              {pagination?.total ?? 0} countries
            </Typography>
            {/* Счётчик просроченных живёт прямо в строке поиска и работает как ярлык фильтра.
                Подпись держим в text.primary: warning.main и warning.dark дают на белом 3.11:1
                и 3.79:1 — ниже порога 4.5:1 для мелкого текста. Смысл несут иконка, рамка и текст */}
            {/* Показываем и когда фильтр включён — иначе его нечем будет выключить */}
            {(needsCheckOnly || needsCheckCount > 0) && (
              <Tooltip
                title={
                  needsCheckOnly
                    ? 'Filter is on — click to show all countries'
                    : 'Show only countries that need a national calendar check'
                }
              >
                <Chip
                  size="small"
                  variant="outlined"
                  color="warning"
                  clickable
                  icon={<HourglassBottom />}
                  label={
                    needsCheckCount === 1 ? '1 needs a calendar check' : `${needsCheckCount} need a calendar check`
                  }
                  onClick={handleToggleNeedsCheck}
                  aria-pressed={needsCheckOnly}
                  data-active={needsCheckOnly ? 'true' : undefined}
                  sx={NEEDS_CHECK_CHIP_SX}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      </Paper>
      <Box mt={1}>
        <CountriesTablePaginated
          list={countriesList}
          pagination={pagination}
          page={page}
          rowsPerPage={rowsPerPage}
          sortBy={sortBy}
          sortOrder={sortOrder}
          isLoading={isFetching}
          loadingDiseaseId={loadingDiseaseId}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortChange}
          onDiseaseClick={handleDiseaseClick}
          onCountryInfoClick={handleCountryInfoClick}
        />
      </Box>

      {/* Edit Disease Dialog */}
      {selectedDiseaseId && (
        <EditDiseaseDialog
          diseaseId={selectedDiseaseId}
          userRole={userData?.role}
          isLoading={isUpdating}
          preselectedCountryCode={selectedCountryCode}
          onClose={handleCloseDiseaseDialog}
          onVerified={updateDisease}
          onSubmit={updateDisease}
          onDataLoaded={handleDataLoaded}
        />
      )}

      {/* Country National Calendar Dialog */}
      {selectedInfoCountry && (
        <CountryInfoDialog
          key={selectedInfoCountry.countryCode}
          country={selectedInfoCountry}
          isSaving={isSavingInfo}
          onClose={handleCloseInfoDialog}
          onSubmit={handleSaveCountryInfo}
        />
      )}
    </Box>
  );
};
